import { Text } from '@/components/text';
import { Portal } from '@/components/ui/portal';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { LayoutRectangle, Platform, StyleSheet, View } from 'react-native';

export interface TooltipProps {
    content?: string;
    component?: ReactNode;
    children: ReactNode;
    disabled?: boolean;
    fullWidth?: boolean;
    side?: 'top' | 'bottom';
    align?: 'left' | 'center' | 'right';
}

export function Tooltip({ content, component, children, disabled, fullWidth, side = 'top', align = 'center' }: TooltipProps) {
    const [visible, setVisible] = useState(false);
    const [layout, setLayout] = useState<LayoutRectangle | null>(null);
    const triggerRef = useRef<View>(null);

    if (disabled) return <>{children}</>;

    const show = () => {
        if (Platform.OS === 'web') {
            updateLayout();
        } else {
            triggerRef.current?.measureInWindow((x, y, width, height) => {
                setLayout({ x, y, width, height });
                setVisible(true);
            });
        }
    };

    const updateLayout = () => {
        const node = (triggerRef.current as any)?.getScrollableNode?.() || triggerRef.current;
        if (node && node.getBoundingClientRect) {
            const rect = node.getBoundingClientRect();
            setLayout({
                x: rect.left,
                y: rect.top,
                width: rect.width,
                height: rect.height
            });
            setVisible(true);
        }
    };

    useEffect(() => {
        if (!visible || Platform.OS !== 'web') return;

        // Tooltips should close on scroll because the pointer will no longer be over the trigger
        const handleScroll = () => hide();
        const handleResize = () => updateLayout();

        window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll, { capture: true });
            window.removeEventListener('resize', handleResize);
        };
    }, [visible]);

    const hide = () => setVisible(false);

    const events = Platform.OS === 'web' ? {
        onMouseEnter: show,
        onMouseLeave: hide,
    } : {
        onPressIn: show,
        onPressOut: hide,
    };

    return (
        <View
            ref={triggerRef}
            style={[
                styles.container,
                fullWidth && { width: '100%', alignItems: 'stretch' }
            ]}
            {...(events as any)}
        >
            {children}

            {visible && layout && (
                <Portal>
                    <View
                        pointerEvents="none"
                        style={[
                            styles.tooltip,
                            {
                                position: Platform.OS === 'web' ? 'fixed' : 'absolute',
                                top: side === 'top' ? layout.y - 6 : layout.y + layout.height + 6,
                                left: align === 'left' ? layout.x : align === 'right' ? layout.x + layout.width + 10 : layout.x + layout.width / 2,
                                transform: [
                                    { translateX: align === 'left' ? '0%' : align === 'right' ? '-100%' : '-50%' },
                                    ...(side === 'top' ? [{ translateY: '-100%' }] : [])
                                ] as any
                            }
                        ]}>
                        {content && <Text style={styles.text}>{content}</Text>}
                        {component && component}
                        <View style={[
                            styles.arrow,
                            side === 'top' ? styles.arrowTop : styles.arrowBottom,
                            align === 'left' && { left: 20, marginLeft: -5 },
                            align === 'right' && { left: 'auto', right: 12, marginLeft: 0, marginRight: 0 },
                        ]} />
                    </View>
                </Portal>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        position: 'relative',
    },
    tooltip: {
        position: 'absolute',
        backgroundColor: '#333',
        paddingHorizontal: 10,
        paddingVertical: 8,
        borderRadius: 8,
        zIndex: 999999,
        width: 200,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 10,
    },
    text: {
        color: 'white',
        fontSize: 12,
        lineHeight: 16,
        textAlign: 'center',
    },
    arrow: {
        position: 'absolute',
        left: '50%',
        marginLeft: -5,
        borderWidth: 5,
        borderColor: 'transparent',
    },
    arrowTop: {
        top: '100%',
        borderTopColor: '#333',
    },
    arrowBottom: {
        bottom: '100%',
        borderBottomColor: '#333',
    },
});
